import { delay, generateUuid, get, post } from '@chat/utils';
import { chain, includes } from 'lodash';
import { Dispatch } from 'redux';
import { encrypt } from '@/utils/encryptor';
import { reportTimeout } from '@/utils/reportTimeout';
import {
    FAQ_CHECKLABEL,
    FAQ_COUNT,
    FAQ_INIT,
    FAQ_REPORT,
    FAQ_SEND,
    FAQ_SOLVE,
    FAQ_UNSOLVE_REASON,
    FAQ_WORKFLOWS_RUN,
    isFAQSopWorkflow,
    setFAQWorkflowFlow
} from '@/api/url';
import { faqChoice, faqResolve } from '@/helpers/faq';
import { withMessageTime } from '@/helpers/imContext';
import { setLastTime } from '@/redux/im/action';
import { MyThunkResult } from '@/typings/reducer';
import {
    PARAM_FAQ_COUNT,
    PARAM_FAQ_LIB,
    PARAM_FAQ_REASON,
    PARAM_FAQ_SOLVE
} from '@/typings/params';
import { IM_CTX, IM_MESSAGE } from '@/typings/global';
import { IMSetFaqMessageAction, IMUpdateFaqServerContentAction } from '@/redux/im/actionType';
import {
    FAQChangeFaqStatusAction,
    FAQReloadFaqAction,
    FAQSetFaqSessionAction,
    FAQSetStageClickAction,
    FAQ_TYPE
} from './actionType';
import {
    parseWorkflowReplyPayload,
    workflowSSERequest,
    WORKFLOW_ABORT,
    WORKFLOW_EMPTY_RESULT,
    WORKFLOW_TIMEOUT
} from '@/utils/WorkflowSSEUtil';
import { DIFY_REQUEST_TIMEOUT } from '@/config/config';

const ALLOWED_HISTORY_TYPES = ['in', 'out', 'faq-init', 'faq-resolve', 'faq-art'];

let workflowConversationId = '';

const normalizeHistoryMessage = (item: IM_MESSAGE): string => {
    const rawContent =
        item.type === 'faq-resolve'
            ? (item.ctx as IM_CTX)?.answer
            : item.server_content || (item.message as string);

    return typeof rawContent === 'string' ? rawContent : '';
};

const buildWorkflowContext = (
    messageList: IM_MESSAGE[],
    faqUniqueId: string
): { chat_id: string; history: Array<{ role: 'user' | 'assistant'; content: string }> } => ({
    chat_id: faqUniqueId,
    history: chain(messageList)
        .filter((item) => includes(ALLOWED_HISTORY_TYPES, item.type))
        .dropRight(1)
        .map((item) => ({
            role: (item.type === 'out' ? 'user' : 'assistant') as 'user' | 'assistant',
            content: normalizeHistoryMessage(item)
        }))
        .value()
});

/**
 * @param {boolean} bol
 * 控制faq功能开启关闭
 */
export const faqchangeStatus =
    (bol: boolean): MyThunkResult<unknown, FAQChangeFaqStatusAction> =>
    (dispatch: Dispatch<FAQChangeFaqStatusAction>) => {
        dispatch({
            type: FAQ_TYPE.CHANGE_FAQ_STATUS,
            payload: bol
        });
    };
/**
 * @param {*} bol
 * 阶段转换按钮点击
 */
export const clickstage =
    (bol: boolean): MyThunkResult<unknown, FAQSetStageClickAction> =>
    (dispatch: Dispatch<FAQSetStageClickAction>) => {
        dispatch({
            type: FAQ_TYPE.SET_STAGE_CLICK,
            payload: bol
        });
    };
/**
 * faq注入
 */
export const faqinit =
    (): MyThunkResult<unknown, FAQSetFaqSessionAction> =>
    async (dispatch: Dispatch<FAQSetFaqSessionAction>, getState) => {
        try {
            IM.DifyServer.resetWorkflowState();
            const {
                base_config: {
                    game,
                    welcome,
                    faq_unique_id,
                    game: { ai_status }
                }
            } = getState().IndexReducer;
            const res = await get(FAQ_INIT, {
                game_id: game.id,
                platform: game.platform,
                faq_unique_id
            });
            await setLastTime({ compare: 'immidiate' })(dispatch);
            const { welcome: faqWelcome, hot_faq, log_id, flow } = res.data.d;
            setFAQWorkflowFlow(flow);
            IM.IMServer.lists([
                {
                    message: welcome?.message ?? (ai_status === 0 ? '' : faqWelcome),
                    type: 'faq-init',
                    ctx:
                        ai_status === 0 ? (hot_faq || []).map((item) => ({ ...item, log_id })) : [],
                    avatar: 'faq',
                    nick: 'faq'
                }
            ]);
            dispatch({
                type: FAQ_TYPE.SET_FAQ_SESSION,
                payload: res.data.d
            });
            return await Promise.resolve(res.data.d);
        } catch (err) {
            IM.IMServer.notice('error', err?.data?.e || err);
            return Promise.reject(err);
        }
    };
/**
 * 获取faq记录id
 */
export const getlogid =
    ({ input, faq_ids }: PARAM_FAQ_LIB): MyThunkResult<unknown, unknown> =>
    (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const {
                base_config: { game, faq_unique_id }
            } = getState().IndexReducer;
            const data = {
                input,
                faq_ids,
                faq_unique_id,
                game_id: game.id
            };
            post(FAQ_CHECKLABEL, data)
                .then((res) => resolve(res.data.d))
                .catch((err) => {
                    IM.IMServer.notice('error', err?.data?.e || err);
                    return reject(err);
                });
        });
/**
 * @param {*} faq_id 问题id
 * @param {*} pid 日志id
 * 问题计数
 */
export const getcounted =
    ({ faq_id, pid }: PARAM_FAQ_COUNT): MyThunkResult<unknown, unknown> =>
    (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const {
                base_config: { game, faq_unique_id }
            } = getState().IndexReducer;
            const data = {
                faq_id,
                pid,
                faq_unique_id,
                game_id: game.id
            };
            post(FAQ_COUNT, data)
                .then((res) => resolve(res.data.d))
                .catch((err) => {
                    IM.IMServer.notice('error', err?.data?.e || err);
                    return reject(err);
                });
        });

export const faqUpdateServerContent =
    ({
        id,
        content
    }: {
        id: number | string;
        content: string;
    }): MyThunkResult<unknown, IMUpdateFaqServerContentAction> =>
    (dispatch: Dispatch<IMUpdateFaqServerContentAction>) => {
        dispatch({
            type: FAQ_TYPE.UPDATE_FAQ_SERVER_CONTENT,
            payload: { id, content }
        });
    };

/**
 * @param {*} input 用户输入内容
 * faq发送
 */
export const getfaq =
    (
        input: string,
        options: {
            dify?: number;
            checkRequestDify?: boolean;
            messageId?: IM_MESSAGE['id'];
        } = {}
    ): MyThunkResult<unknown, unknown> =>
    (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const {
                base_config: { game, faq_unique_id }
            } = getState().IndexReducer;
            const { stage, message_list } = getState().IMReducer;
            const { request_type } = getState().FaqReducer;
            const { checkRequestDify = false, messageId, ...requestOptions } = options;
            const sopHit = IM.DifyServer.getSopHit();
            const context = JSON.stringify(buildWorkflowContext(message_list, faq_unique_id));
            const data = {
                input,
                context,
                faq_unique_id,
                game_id: game.id,
                status: stage,
                request_type,
                ...(sopHit === undefined ? {} : { sop_hit: sopHit }),
                ...requestOptions
            };
            post(FAQ_SEND, data)
                .then(async (res) => {
                    if (sopHit !== undefined) {
                        IM.DifyServer.clearSopHit();
                    }
                    const serverContent = res.data.d?.input;
                    if (messageId && serverContent) {
                        faqUpdateServerContent({ id: messageId, content: serverContent })(dispatch);
                    }
                    if (checkRequestDify && res.data.d?.request_dify === true) {
                        return resolve(res.data.d);
                    }
                    const msg = await faqChoice(res.data.d, res.data.d?.list, 'faq');
                    const messages = _.isArray(msg) ? msg : [msg];
                    messages.forEach((m) =>
                        dispatch({ type: FAQ_TYPE.SET_FAQ_MESSAGE, payload: withMessageTime(m) })
                    );
                    return resolve(res.data.d);
                })
                .catch((err) => {
                    IM.IMServer.notice('error', err?.data?.e || err);
                    return reject(err);
                });
        });

/**
 * @param {*} input 用户输入内容
 * @param {*} report_dify_result 是否上报到dify(1 表示之前有缓存错误，需要重新上报)
 * @param {*} messageId 用户消息id，用于FAQ兜底时回填服务端内容
 * @param {*} context FAQ 后端脱敏后返回的上下文
 * @param {*} authQuery FAQ 后端 token 字段下发的完整 Dify 鉴权查询串
 * 发送faq消息开启ai工作流
 */
export const faqWorkflowsRun =
    (
        input: string,
        report_dify_result = 0,
        messageId?: IM_MESSAGE['id'],
        context = '',
        authQuery = ''
    ): MyThunkResult<unknown, unknown> =>
    (dispatch, getState) =>
        new Promise<void>((resolve, reject) => {
            const {
                base_config: {
                    user_info: { user_id },
                    faq_unique_id,
                    game: { key, platform, name }
                }
            } = getState().IndexReducer;
            const { dify_query_params, model, no_answer_count, transfer_human_threshold } =
                getState().FaqReducer;
            const id = generateUuid(8, 16);
            const { text, replacements } = IM.DifyServer.normalizeFacePlaceholders(input);
            const files = IM.DifyServer.buildDifyFiles(input);
            const msgStack = IM.DifyServer.buildMsgStack();
            IM.DifyServer.markWorkflowInput(text);
            dispatch({
                type: FAQ_TYPE.SET_FAQ_MESSAGE,
                payload: { message: null, type: 'faq-loading', id }
            });
            workflowSSERequest({
                url: FAQ_WORKFLOWS_RUN(),
                authQuery,
                body: {
                    query: text,
                    ...(isFAQSopWorkflow() && workflowConversationId
                        ? { conversation_id: workflowConversationId }
                        : {}),
                    // @ts-ignore
                    user: user_id,
                    files,
                    inputs: {
                        mark_id: id,
                        project_key: dify_query_params.platform_key ?? platform,
                        game_key: key,
                        game_name: name,
                        game_type: '1',

                        faq_unique_id,
                        model,
                        ...dify_query_params,
                        work_id: 'Normal',
                        context,
                        text,
                        // Dify 工作流通过 msg_stack 使用历史被打断消息，当前输入由 query 传递。
                        msg_stack: msgStack,
                        sensitive_map: JSON.stringify(replacements)
                    },
                    // user_id,
                    response_mode: 'streaming'
                },
                // @ts-ignore
                timeout: window.timeout || DIFY_REQUEST_TIMEOUT,
                debug: false
            })
                .then(async (res) => {
                    // 工作流已正常完成时，认为本批输入已被 Dify 消费；即使 reply 为空也清空。
                    IM.DifyServer.completeWorkflow();
                    if (isFAQSopWorkflow() && res.conversation_id) {
                        workflowConversationId = res.conversation_id;
                    }
                    // 解析出 dify 返回内容中的 json
                    const json = parseWorkflowReplyPayload(res.fullText);
                    if (isFAQSopWorkflow()) {
                        IM.DifyServer.setSopHit(json.sop_hit);
                    }
                    // 默认不转人工，只有无答案次数达到阈值时才转人工
                    let isHuman = false;
                    // 判断是否为无答案情况（D1:无依据/弱相关，D2:规则冲突）
                    const isNoAnswer =
                        json.need_human && (json.reason_type === 'D1' || json.reason_type === 'D2');
                    let currentNoAnswerCount = no_answer_count;

                    if (isNoAnswer) {
                        currentNoAnswerCount = no_answer_count + 1;
                        dispatch({
                            type: FAQ_TYPE.UPDATE_NO_ANSWER_COUNT,
                            payload: currentNoAnswerCount
                        });

                        // 达到阈值时自动转人工（阈值大于0时才启用自动转人工）
                        if (
                            transfer_human_threshold > 0 &&
                            currentNoAnswerCount >= transfer_human_threshold
                        ) {
                            isHuman = true;
                        }
                        // need_human 为 true 时显示人工客服按钮
                    } else if (json.need_human) {
                        isHuman = true;
                    }

                    // 3387 等业务侧文本过滤（仍为纯文本）
                    const filteredReply = IM.DifyServer.filter3387(json.reply ?? '');
                    // 将 Dify 返回的文件存储地址替换为客服侧公开访问地址
                    const normalizedReply = IM.DifyServer.normalizeDifyFileUrls(filteredReply);
                    // 将dify输出的纯文本中的链接转成html  http(s) → <img>/<a>，换行 → <br/>
                    const answer = IM.DifyServer.textToHtml(normalizedReply);
                    const msg: IM_MESSAGE = {
                        message: isHuman ? answer : null,
                        type: isHuman ? 'faq-art' : 'faq-resolve',
                        ctx: {
                            answer,
                            log_id: id,
                            start: json.need_human ? 1 : 0,
                            dify: 1,
                            workflow_run_id: res.workflow_run_id,
                            ...res
                        },
                        id
                    };
                    // 需要根据report_dify_result 来判断是否需要上报到dify，是1 则必须dify接口通不通过都要告知后端
                    if (report_dify_result === 1) {
                        reportTimeout(faq_unique_id, encrypt, true).catch(() => {});
                    }
                    dispatch({ type: FAQ_TYPE.SET_FAQ_MESSAGE, payload: withMessageTime(msg) });
                    return resolve();
                })
                .catch((err) => {
                    console.log('err222', err);
                    // 工作流明确结束但没有正文时，按业务约定视为已消费并清空。
                    if (err?.[WORKFLOW_EMPTY_RESULT]) {
                        IM.DifyServer.completeWorkflow();
                        // 其他非主动中断的失败保留当前输入，等待下一次工作流继续补发。
                    } else if (!err?.[WORKFLOW_ABORT]) {
                        IM.DifyServer.preserveWorkflowInput(text);
                    }
                    // 超时统计上报 - 异步执行，失败不影响主流程
                    if (err?.[WORKFLOW_TIMEOUT]) {
                        reportTimeout(faq_unique_id, encrypt, false).catch(() => {});
                    }
                    // 用户取消或触发人工关键词：移除 loading；被新请求取消：新请求自带 loading，不移除
                    if (err?.[WORKFLOW_ABORT]) {
                        if (!err?.abortedForNewRequest) {
                            dispatch({ type: FAQ_TYPE.REMOVE_FAQ_LOADING });
                        }
                        return resolve();
                    }
                    dispatch({ type: FAQ_TYPE.REMOVE_FAQ_LOADING });
                    // 超时，等其他错误都走兜底faq请求
                    getfaq(input, { dify: 0, messageId })(dispatch, getState);
                    return resolve();
                });
        });

/**
 * 插入faq消息
 * @param {*} item
 */
export const faqappend =
    (item: IM_MESSAGE): MyThunkResult<unknown, IMSetFaqMessageAction> =>
    async (dispatch: Dispatch<IMSetFaqMessageAction>, getState) => {
        const message = withMessageTime(item);
        IM.flickerStart();
        const { last_time } = getState().IMReducer;
        await setLastTime({ last_time, compare: 'delay' })(dispatch);
        dispatch({ type: FAQ_TYPE.SET_FAQ_MESSAGE, payload: message });
    };
/**
 * 解决/未解决
 * @param {*} s 0:未解决 1:解决
 * @param {*} log_id 日志id
 * @param {*} faq_id 问题id
 */
export const getresolve =
    ({ solve, log_id, faq_id, dify = 0 }: PARAM_FAQ_SOLVE): MyThunkResult<unknown, unknown> =>
    async (dispatch, getState) => {
        try {
            const { stage } = getState().IMReducer;
            const {
                base_config: { faq_unique_id }
            } = getState().IndexReducer;
            const data = {
                solve,
                log_id,
                faq_id,
                dify,
                status: stage,
                faq_unique_id
            };
            const res = await post(FAQ_SOLVE, data);
            const msg = faqResolve({ ...res.data.d, solve });
            // 接入人工延时插入
            if (msg.type === 'faq-art') {
                delay(2000).then(async () => {
                    await faqappend(msg)(dispatch, getState);
                });
            } else {
                await faqappend(msg)(dispatch, getState);
            }
            return await Promise.resolve(res.data.d);
        } catch (err) {
            IM.IMServer.notice('error', err?.data?.e || err);
            return Promise.reject(err);
        }
    };

/**
 * 未解决原因选择
 * @param {*} log_id 日志id
 * @param {*} reason_id 原因id
 */
export const getreason =
    ({ log_id, reason_id, dify = 0 }: PARAM_FAQ_REASON): MyThunkResult<unknown, unknown> =>
    (dispatch, getState) =>
        new Promise<void>((resolve, reject) => {
            const { stage } = getState().IMReducer;
            const {
                base_config: { faq_unique_id }
            } = getState().IndexReducer;
            const data = {
                log_id,
                dify,
                status: stage,
                reason_id,
                faq_unique_id
            };
            post(FAQ_UNSOLVE_REASON, data)
                .then((res) => {
                    const msg: IM_MESSAGE = { message: res.data.d.message, type: 'in' };
                    faqappend(msg)(dispatch, getState);
                    return resolve();
                })
                .catch((err) => {
                    IM.IMServer.notice('error', err?.data?.e || err);
                    return reject(err);
                });
        });
/**
 * 重制faq状态
 */
export const reloadfaq =
    (): MyThunkResult<unknown, FAQReloadFaqAction> => (dispatch: Dispatch<FAQReloadFaqAction>) => {
        dispatch({
            type: FAQ_TYPE.RELOAD_FAQ
        });
    };
